import { Bericht, GeneriekBericht, SmsBericht } from "@/entities/bericht";
import { FunctieAvatar, NaamFunctieTrein } from "@/features/contactpersonen";
import { Badge, Separated, Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components";
import { formatDateTimeRelative } from "@/shared/utils";

type BerichtListProps = {
    berichten: Bericht[];
    showOnderwerpBadge: boolean;
};

export function BerichtList({ berichten, showOnderwerpBadge }: BerichtListProps) {
    return (
        <ol className="border-b" data-testid="berichten-list">
            <Separated>
                {berichten.map((bericht) => (
                    <BerichtItem bericht={bericht} key={bericht.id} showOnderwerpBadge={showOnderwerpBadge} />
                ))}
            </Separated>
        </ol>
    );
}

type BerichtProps = {
    bericht: Bericht;
    showOnderwerpBadge: boolean;
};

function BerichtItem({ bericht, showOnderwerpBadge }: BerichtProps) {
    if (bericht.soort === "Sms") {
        return <SmsBerichtItem bericht={bericht} />;
    }

    return <GeneriekBerichtItem bericht={bericht} showOnderwerpBadge={showOnderwerpBadge} />;
}

type GeneriekBerichtProps = {
    bericht: GeneriekBericht;
    showOnderwerpBadge: boolean;
};

function GeneriekBerichtItem({ bericht, showOnderwerpBadge }: GeneriekBerichtProps) {
    return (
        <li className="py-2">
            <article className="grid">
                <div className="flex items-center gap-2">
                    <BerichtTijd date={bericht.verstuurdOp} />
                    {showOnderwerpBadge && (
                        <Badge variant="gray-subtle" className="ring-border ring-1 ring-inset">
                            {bericht.onderwerp}
                        </Badge>
                    )}
                </div>
                <p className="text-s wrap-anywhere whitespace-pre-line" data-testid="generiek-bericht-text">
                    {bericht.bericht}
                </p>
            </article>
        </li>
    );
}

type SmsBerichtProps = {
    bericht: SmsBericht;
};

function SmsBerichtItem({ bericht }: SmsBerichtProps) {
    return (
        <li className="flex flex-row gap-2 py-2">
            <FunctieAvatar functies={bericht.ontvangers.map((o) => o.functie)} />
            <article className="grid">
                <header className="flex gap-2">
                    <div className="flex flex-wrap gap-x-1">
                        {bericht.ontvangers.map((contact, index) => (
                            <Tooltip key={contact.id}>
                                <TooltipTrigger>
                                    <NaamFunctieTrein key={contact.id} contact={contact} isLast={index === bericht.ontvangers.length - 1} />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-none text-nowrap">Telefoonnummer: {contact.telefoonnummer}</TooltipContent>
                            </Tooltip>
                        ))}
                    </div>
                    <BerichtTijd date={bericht.verstuurdOp} />
                </header>
                <section>
                    <p className="text-s leading-l font-normal" data-testid="sms-bericht-text">
                        {bericht.bericht}
                    </p>
                </section>
            </article>
        </li>
    );
}

type BerichtTijdProps = {
    date: Date;
};

function BerichtTijd({ date }: BerichtTijdProps) {
    return (
        <time className="text-s text-content-subtle mt-px flex items-center font-medium text-nowrap" dateTime={date.toISOString()}>
            {formatDateTimeRelative(date)}
        </time>
    );
}
